import React, { ReactNode, useState, useRef } from 'react';
import { useDemoMode } from '../../hooks/useDemoMode';

interface DemoDisableOverlayProps {
  children: ReactNode;
  showTooltip?: boolean;
  tooltipMessage?: string;
  /** Override demo detection - force enable/disable */
  isDisabled?: boolean;
}

/**
 * Demo-aware overlay component that automatically disables interactions
 * when in demo mode. Can also be used as a general disable overlay.
 */
const DemoDisableOverlay: React.FC<DemoDisableOverlayProps> = ({
  children,
  showTooltip = true,
  tooltipMessage,
  isDisabled
}) => {
  const { isDemoMode, demoAccessKey } = useDemoMode();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Disable when in demo mode or explicitly disabled
  const shouldDisable = isDisabled !== undefined ? isDisabled : isDemoMode;

  // Default message
  const defaultMessage = "Sign up to use this feature";
  const finalMessage = tooltipMessage || defaultMessage;

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!overlayRef.current) return;
    
    const rect = overlayRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (!shouldDisable) return <>{children}</>;

  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
      {children}
      <div
        ref={overlayRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'transparent',
          zIndex: 999,
          cursor: 'not-allowed'
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (showTooltip) {
            // Dispatch event to show signup modal
            const event = new CustomEvent('show-demo-signup', {
              detail: {
                demoAccessKey: demoAccessKey || new URLSearchParams(window.location.search).get('demoaccesskey'),
                message: finalMessage
              }
            });
            window.dispatchEvent(event);
          }
        }}
        onMouseDown={(e) => e.preventDefault()}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      
      {/* Dynamic tooltip that follows mouse */}
      {showTooltip && isHovered && (
        <div
          style={{
            position: 'absolute',
            left: mousePosition.x + 10,
            top: mousePosition.y < 45 ? mousePosition.y + 20 : mousePosition.y - 35,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            pointerEvents: 'none',
            zIndex: 1000,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            // Prevent tooltip from going outside the container
            transform: mousePosition.x > 200 ? 'translateX(-100%) translateX(-20px)' : 'none',
            maxWidth: '200px',
            wordWrap: 'normal',
          }}
        >
          {finalMessage}
          <div
            style={{
              position: 'absolute',
              top: mousePosition.y < 45 ? '-5px' : '100%',
              left: mousePosition.x > 200 ? 'calc(100% - 20px)' : '10px',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: mousePosition.y < 45 ? 'none' : '5px solid rgba(0, 0, 0, 0.9)',
              borderBottom: mousePosition.y < 45 ? '5px solid rgba(0, 0, 0, 0.9)' : 'none',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DemoDisableOverlay;